import React, { useCallback, useRef, createRef, useState, useEffect, Fragment } from 'react';

import { getUrlParam, usePrevious } from '../../utils';
import {
    customersGetCommunicationPreferences,
    customersPutCommunicationPreferences,
    customersResendEmailPreferences
} from '../../api';

import { Heading, Text, className } from 'aplazame-design-system';
import { default as ButtonComponent } from 'aplazame-design-system/src/components/GatsbyTransitionButtonComponent';
import { useTranslate } from 'soluble-gatsby-source';

import FormInput from '../FormInput';
import FormSubmit from '../FormSubmit';
import FormAlert from '../FormAlert';

import style from './style.module.scss';


export default function FormCommunications({
    onLoadToken,
    onPreventExit,
    onResendEmail
}) {
    const translate = useTranslate();
    const [ token, setToken ] = useState(null);
    const [ preferences, setPreferences ] = useState(null);
    const [ initialPreferences, setInitialPreferences ] = useState(null);
    const [ allowSubmit, setAllowSubmit ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ success, setSuccess ] = useState(false);
    const prevPreferences = usePrevious(preferences);
    const checkboxesRef = useRef([ createRef(), createRef(), createRef(), createRef() ]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const preventCloseListener = (e) => {
            if (allowSubmit) {
                e.preventDefault();
                e.returnValue = '';
            }
        }

        window.addEventListener('beforeunload', preventCloseListener);


        const preventMoveListener = (e) => {
            if (allowSubmit) {
                e.preventDefault();
                let href = e.target.href;
                if (!href && e.target.parentNode) {
                    href = e.target.parentNode.href;
                    if (!href && e.target.parentNode.parentNode) {
                        href = e.target.parentNode.parentNode;
                    }
                }

                onPreventExit({
                    onExit: () => {
                        removePreventMoveListeners();
                        window.removeEventListener('beforeunload', preventCloseListener);

                        if (href) {
                            window.location.href = href;
                        }
                    }
                });
            }
        }

        const addPreventMoveListeners = () => {
            [ ...window.document.body.querySelectorAll('a') ].forEach(a => {
                a.addEventListener('click', preventMoveListener);
            });
        }

        const removePreventMoveListeners = () => {
            [ ...window.document.body.querySelectorAll('a') ].forEach(a => {
                a.removeEventListener('click', preventMoveListener);
            });
        }

        addPreventMoveListeners();
        
        return () => {
            window.removeEventListener('beforeunload', preventCloseListener);
            removePreventMoveListeners();
        }
    }, [ allowSubmit, onPreventExit ]);

    useEffect(() => {
        const token = getUrlParam('token');
        
        if (token) {
            setToken(token);
            
            customersGetCommunicationPreferences(token)
                .then(setPreferences)
                .catch(() => {})
                .finally(onLoadToken);
        } else {
            onLoadToken();
        }
    }, [ onLoadToken, setPreferences, setToken ]);

    useEffect(
        () => {
            if (!preferences) {
                if (prevPreferences) {
                    setAllowSubmit(false);
                    setInitialPreferences(null);
                }

                return;
            }

            if (preferences && !prevPreferences) {
                Object.keys(preferences).forEach((pref, i) => {
                    if (checkboxesRef.current[i].current) {
                        checkboxesRef.current[i].current.setValue(preferences[pref]);
                    }
                });
                
                setInitialPreferences({ ...preferences });
            } else {
                setAllowSubmit(JSON.stringify(preferences) !== JSON.stringify(initialPreferences));
            }
        },
        [ checkboxesRef, preferences, prevPreferences, setInitialPreferences, initialPreferences, setAllowSubmit ]
    );

    const updatePreferencesState = useCallback(
        () => {
            if (!preferences) {
                return;
            }

            let newPreferences = { ...preferences };

            Object.keys(preferences).forEach((pref, i) => {
                if (checkboxesRef.current[i].current) {
                    newPreferences[pref] = checkboxesRef.current[i].current.getValue() ? true : false;
                }
            });

            setPreferences(newPreferences);
        },
        [ checkboxesRef, preferences, setPreferences, initialPreferences ]
    );
    
    const submitForm = useCallback(
        () => {
            if (!allowSubmit) {
                return;
            }

            setLoading(true);

            customersPutCommunicationPreferences(token, preferences)
                .then(() => {
                    setInitialPreferences(preferences);
                    setSuccess(true);
                })
                .catch(() => {
                    setPreferences(null);
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [ allowSubmit, preferences, token, setLoading, setInitialPreferences, setSuccess ]
    );

    useEffect(() => {
        let timeout = null;

        if (success) {
            timeout = setTimeout(() => {
                setSuccess(false);
            }, 3000);
        }

        return () => {
            if (timeout) {
                window.clearTimeout(timeout);
            }
        }
    }, [ success, setSuccess ]);

    const resendEmail = useCallback(
        () => {
            setLoading(true);
            
            customersResendEmailPreferences(token)
                .then(() => {
                    onResendEmail();
                });
        },
        [ token, setLoading, onResendEmail ]
    );

    return (
        <>
            <Heading tag="h2" className={`h3 mb-4 ${preferences ? 'mb-lg-9' : ''} font-weight-bold`}>
                {translate['communications-page.hero.form.title'].text}
            </Heading>
            {preferences ? (
                <>
                    {Object.keys(preferences).map((pref, i) => (
                        <Fragment key={i}>
                            <FormInput
                                ref={checkboxesRef.current[i]}
                                type="checkbox"
                                name={pref}
                                label={translate[`communications-page.hero.form.${pref}.label`] ? translate[`communications-page.hero.form.${pref}.label`].md : pref}
                                onChange={() => {
                                    setTimeout(updatePreferencesState, 0);
                                }}
                                labelPosition="after"
                            />
                            {Object.keys(preferences).length - 1 !== i ? <hr {...className(`${style.separator} mt-3 mb-3 mt-lg-6 mb-lg-6`)} /> : null}
                        </Fragment>
                    ))}
                    <div {...className(`pt-5 pt-lg-4 ${style['btn-wrapper']}`)}>
                        <FormSubmit
                            text={translate['communications-page.hero.form.submit'].text}
                            loading={loading}
                            onClick={submitForm}
                            allowSubmit={allowSubmit}
                        />
                    </div>
                </>
            ) : (
                <>
                    <FormAlert>
                        {translate['communications-page.hero.form.expired.alert'].text}
                    </FormAlert>
                    <Text tag="div" className="body mt-3 mt-lg-4 mb-2 mb-lg-3">
                        {translate['communications-page.hero.form.expired.resend.title'].text}
                    </Text>
                    <ButtonComponent
                        buttonClassName={style['resend-button']}
                        button={{
                            detached: false,
                            Tag: 'button',
                            onClick: resendEmail,
                            disabled: loading,
                            label: translate['communications-page.hero.form.expired.resend.cta'].text,
                        }}
                    />
                    <hr {...className(`${style.separator} mt-2 mt-lg-3 mb-4 mb-lg-5`)} />
                    <Text tag="div" className={`body ${style.footer}`}>
                        {translate['communications-page.hero.form.expired.footer'].md}
                    </Text>
                </>
            )}
            <div {...className(`${style['alert-wrapper']} ${success ? style['alert-wrapper--show'] : ''}`)}>
                <Text className={`h4 ${style.alert}`} tag="div">
                    {translate['communications-page.alert.success'].text}
                </Text>
            </div>
        </>
    );
}